import {
  getPurchaseDailyByWebcoin,
  purchaseAvatarAsync,
  purchaseFrameAsync,
  purchaseEnergyPackAsync,
} from './api';
import { NotSufficientWebcoinsError, UnknownError } from './exception';
import { useMutation } from '@tanstack/react-query';
import { useCallback } from 'react';
import { AlternativePurchaseTypes, PurchasingOffer } from './type';
import { AxiosResponse, isAxiosError } from 'axios';
import { useGetWebcoins } from '@wwe-portal/web/providers/queries/currency';
import { checkOfferValiditiy } from '../client/offer';

interface UsePurchaseByWebcoinProps {
  onError?: (error: unknown) => void;
  persistPurchasingOffer: (data: PurchasingOffer | null) => void;
  onPaymentSuccess?: (
    data: AxiosResponse<any>,
    variables: PurchasingOffer
  ) => void;
}
const usePurchaseByWebCoins = ({
  onError,
  persistPurchasingOffer,
  onPaymentSuccess,
}: UsePurchaseByWebcoinProps) => {
  const handleError = useCallback(
    async (error: unknown) => {
      onError?.(error);
    },
    [onError]
  );

  const { amount } = useGetWebcoins();

  const isNotEnoughWebCoins = (offerData) => {
    return (
      amount == null ||
      (offerData.AlternativeCostAmount != null &&
        amount < offerData.AlternativeCostAmount)
    );
  };

  const handleSuccess = (
    purchaseResult: AxiosResponse<any>,
    params: PurchasingOffer
  ) => {
    const { offerData, alternativePurchaseType } = params;
    verifyAfterPurchase(purchaseResult);
    persistPurchasingOffer({
      offerData,
      offerType: undefined,
      offerId: offerData.OffersID,
      paymentInfo: purchaseResult.data,
      transactionId: Date.now().toString(),
      alternativePurchaseType,
      analyticsData: undefined,
    });
    onPaymentSuccess?.(purchaseResult, params);
  };

  const verifyAfterPurchase = (purchaseResult: AxiosResponse<any, any>) => {
    const paymentInfo = purchaseResult.data;
    if (
      NotSufficientWebcoinsError.isNotSufficientWebcoins(purchaseResult) ||
      !paymentInfo.data.success
    ) {
      throw new NotSufficientWebcoinsError();
    }

    if (!paymentInfo.data.success) {
      throw new UnknownError(paymentInfo.data.message);
    }
  };

  const purchaseThenVerify = async (promise: Promise<AxiosResponse<any>>) => {
    const result = await promise;
    verifyAfterPurchase(result);
    return result;
  };

  const { mutate: purchaseItemsByWebCoins } = useMutation({
    mutationFn: async ({
      offerData,
      alternativePurchaseType,
    }: PurchasingOffer) => {
      persistPurchasingOffer({
        offerId: offerData.OffersID,
        alternativePurchaseType,
        offerData,
        offerType: undefined,
        analyticsData: undefined,
      });

      if (isNotEnoughWebCoins(offerData)) {
        throw new NotSufficientWebcoinsError();
      }

      if (alternativePurchaseType === AlternativePurchaseTypes.Avatar) {
        return purchaseThenVerify(
          purchaseAvatarAsync({
            price: offerData.AlternativeCostAmount ?? 0,
            avatarId: offerData.OffersID ?? '',
          })
        );
      }

      if (alternativePurchaseType === AlternativePurchaseTypes.Frame) {
        return await purchaseThenVerify(
          purchaseFrameAsync({
            price: offerData.AlternativeCostAmount ?? 0,
            frameId: offerData.OffersID ?? '',
          })
        );
      }

      if (alternativePurchaseType === AlternativePurchaseTypes.Energy) {
        return await purchaseThenVerify(
          purchaseEnergyPackAsync({
            name: offerData.OffersID ?? '',
          })
        );
      }

      const validationResult = await checkOfferValiditiy(offerData.OffersID);
      if (!validationResult.data.data.success) {
        throw new Error(validationResult.data.data.message);
      }
      return await purchaseThenVerify(
        getPurchaseDailyByWebcoin({
          offerId: offerData.OffersID ?? '',
        })
      );
    },
    onSuccess: handleSuccess,
    onError: (error) => {
      if (isAxiosError(error) && error.response) {
        try {
          verifyAfterPurchase(error.response);
        } catch (e) {
          handleError(e);
        }
        return;
      }
      handleError(error);
    },
  });

  return {
    purchaseItemsByWebCoins,
  };
};

export default usePurchaseByWebCoins;
